import { Modal, Pagination, Spin, message, Empty } from "antd";
import styles from "../styles/ExchangesModal.module.css";
import { useDispatch, useSelector } from "react-redux";
import { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  openExchangeConnectionSettings,
  setExchangeForConnection,
  setPreviousPath,
} from "../redux/exchange/actions";
import { certificatedExchange } from "../utility/data";
import { LiaCertificateSolid } from "react-icons/lia";
import ExchangeRecommendationModal from "./exchanges/ExchangeRecommendationModal";
import { IoSearch } from "react-icons/io5";
import UpdateExchangeIpModal from "./exchange-ip-setting/UpdateExchangeIpModal";
import * as React from "react";
import EmptyBox from "../ui/EmptyBox";

function ExchangesModal({ show, setShow }) {
  const {
    exchangeState: {
      exchangesInfo,
      exchangesLoading,
      integratedData,
      exchangesList,
    },
  } = useSelector((state) => state);

  const [modalWidth, setModalWidth] = useState(800);
  const itemsPerPage = 12; // Increased number of items per page
  const [currentPage, setCurrentPage] = useState(1);

  const [selectedExchangeName, setSelectedExchangeName] = useState("");
  const [selectedExchangeId, setSelectedExchangeId] = useState("");
  const [showRecommendationModal, setShowRecommendationModal] = useState(false);
  const [searchedExchange, setSearchedExchange] = useState("");
  const [selectedExchange, setSelectedExchange] = useState({});
  const [showIpSettingsModal, setShowIpSettingsModal] = useState(false);

  // Responsive modal width based on window size
  useEffect(() => {
    const handleResize = () => {
      const width = window.innerWidth;
      if (width < 576) {
        setModalWidth("94%");
      } else if (width < 768) {
        setModalWidth(520);
      } else if (width < 992) {
        setModalWidth(680);
      } else {
        setModalWidth(800);
      }
    };

    handleResize(); // Set initial width
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // Filter exchanges based on search term
  const exchanges = exchangesList.filter((ex) =>
    ex?.name.toLowerCase().includes(searchedExchange.toLowerCase()),
  );

  const changeSearchValue = (e) => {
    setCurrentPage(1);
    setSearchedExchange(e.target.value.trimLeft());
  };

  const navigate = useNavigate();
  const dispatch = useDispatch();
  const location = useLocation();

  // Calculate the start and end indices for the current page
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = currentPage * itemsPerPage;

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const goToConnectionPage = (name, id) => {
    setShow(false);
    dispatch(openExchangeConnectionSettings(id));
  };

  const selectExchangeHandler = (e, i, name) => {
    if (e.target.closest("div")) {
      const exchangeId = exchanges?.find((ex) => ex.name === name)?.id;

      setSelectedExchangeId(exchangeId);
      setSelectedExchangeName(name);

      if (exchangeId === "cryptocrispy") {
        return Modal.info({
          content: (
            <h3>
              This is a virtual exchange with no integration required. It comes
              loaded with $1,000 in virtual funds, allowing you to test our bots
              and trade with our AI assistant for free.
            </h3>
          ),
          centered: true,
        });
      }

      const isInWizard = location.pathname.includes("wizard");

      const exchangeData = integratedData?.find(
        (item) => item?.id === exchangeId,
      );

      if (exchangeData) {
        if (
          exchangeData?.status !== "Active" &&
          exchangeData?.ip_status !== "OK"
        ) {
          setSelectedExchange(exchangeData);
          setShowIpSettingsModal(true);
          return;
        }
      }

      if (!isInWizard) {
        if (exchangeData?.status === "Active") {
          setShowRecommendationModal(true);
        } else {
          goToConnectionPage(name, exchangeId);
        }
      } else {
        goToConnectionPage(name, exchangeId);
      }
    }
  };

  return (
    <Modal
      open={show}
      onCancel={() => {
        setShow(false);
        setCurrentPage(1);
      }}
      footer={null}
      centered
      maskClosable={false}
      className={styles.modal}
      width={modalWidth}
      styles={{
        body: { minHeight: "300px" },
        mask: { backdropFilter: "blur(4px)" },
        header: {
          borderBottom: "1px solid #f0f0f0",
          paddingBottom: "12px",
        },
      }}
    >
      <div className={styles.modalContent}>
        <div className={styles.title}>
          <h3>Crypto Exchanges</h3>
          <p>
            The following crypto exchanges are officially supported by
            CryptoCrispy. Select an exchange to connect your account.
          </p>
        </div>

        <div className={styles.searchBox}>
          <IoSearch className={styles.searchIcon} size={18} />
          <input
            value={searchedExchange}
            onChange={changeSearchValue}
            placeholder="Search for an exchange..."
            className={styles.searchInput}
          />
        </div>

        <div className={styles.exchanges}>
          {exchangesLoading && (
            <div className={styles.loader}>
              <Spin size="large" tip="Loading exchanges..." />
            </div>
          )}

          {!exchangesLoading && Object.keys(exchangesInfo).length && (
            <>
              <div className={styles.exchangeList}>
                {exchanges.length > 0 ? (
                  exchanges.slice(startIndex, endIndex).map((exchange, i) => {
                    const isConnected = !!integratedData.find(
                      (item) =>
                        item.name === exchange.name && item.status === "Active",
                    );
                    const isExpired = !!integratedData.find(
                      (item) =>
                        item.name === exchange.name && item.status !== "Active",
                    );
                    const needsIpUpdate = !!integratedData.find(
                      (item) =>
                        item.name === exchange.name &&
                        item.status !== "Active" &&
                        item.ip_status !== "OK",
                    );

                    return (
                      <div
                        className={`${styles.exchangeItem} ${
                          isConnected ? styles.connected : ""
                        } ${isExpired ? styles.expired : ""} ${
                          needsIpUpdate ? styles.ipRequired : ""
                        }`}
                        id={exchange.id}
                        key={exchange.id}
                        onClick={(e) =>
                          selectExchangeHandler(e, i, exchange.name)
                        }
                      >
                        <div className={styles.exchangeImage}>
                          <img
                            src={`https://cryptocrispypro.s3.ap-southeast-2.amazonaws.com/crypto_exchanges_logo/${
                              exchange.id
                            }.png`}
                            alt={exchange.name}
                            onError={(e) => {
                              e.target.onerror = null;
                              e.target.src = "/placeholders/exchange-logo.svg";
                            }}
                          />
                        </div>

                        <div className={styles.exchangeContent}>
                          <span className={styles.exchangeName}>
                            {exchange.name}
                          </span>

                          {isConnected && (
                            <span
                              className={
                                styles.statusBadge + " " + styles.connectedBadge
                              }
                            >
                              Connected
                            </span>
                          )}

                          {isExpired && !needsIpUpdate && (
                            <span
                              className={
                                styles.statusBadge + " " + styles.expiredBadge
                              }
                            >
                              Expired
                            </span>
                          )}

                          {needsIpUpdate && (
                            <span
                              className={
                                styles.statusBadge + " " + styles.ipBadge
                              }
                            >
                              IP Update Required
                            </span>
                          )}
                        </div>
                        {!certificatedExchange.includes(exchange?.id) && (
                          <span className={styles.previewBadge}>Preview</span>
                        )}

                        {/*{certificatedExchange.includes(exchange.id) && (*/}
                        {/*  <div className={styles.certificateContainer}>*/}
                        {/*    <LiaCertificateSolid*/}
                        {/*      title="Certificated"*/}
                        {/*      className={styles.certificatedBadge}*/}
                        {/*    />*/}
                        {/*  </div>*/}
                        {/*)}*/}
                      </div>
                    );
                  })
                ) : (
                  <div className={styles.emptyBox}>
                    {/*<Empty*/}
                    {/*  description="No exchanges found"*/}
                    {/*  image="/placeholders/empty-exchanges.svg"*/}
                    {/*  imageStyle={{ height: 120 }}*/}
                    {/*/>*/}
                    <EmptyBox useCase="table" size="100" />
                    <p>No matched exchanges!</p>
                  </div>
                )}
              </div>

              {exchanges.length > itemsPerPage && (
                <div className={styles.paginationContainer}>
                  <Pagination
                    className={styles.pagination}
                    current={currentPage}
                    total={exchanges.length}
                    pageSize={itemsPerPage}
                    onChange={handlePageChange}
                    showSizeChanger={false}
                    size={window.innerWidth < 576 ? "small" : "default"}
                  />
                </div>
              )}
            </>
          )}
        </div>

        <ExchangeRecommendationModal
          show={showRecommendationModal}
          setShow={setShowRecommendationModal}
          exchange={{ name: selectedExchangeName, id: selectedExchangeId }}
        />
        <UpdateExchangeIpModal
          show={showIpSettingsModal}
          setShow={setShowIpSettingsModal}
          exchangeData={selectedExchange}
        />
      </div>
    </Modal>
  );
}

export default ExchangesModal;
